import { Directive, inject, OnChanges, OnDestroy, OnInit, input } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { WNL_HOST_CONTROL, WnlHostControl } from 'src/app/shared/tokens/wnl-host-control.token';
import { WnlGroupDirective } from './wnl-group.directive';

@Directive({
  selector: '[wnlControl]',
  standalone: true,
})
export class WnlControlDirective implements OnInit, OnChanges, OnDestroy {

  readonly controlKey = input('', { alias: 'wnlControl' }); // (unique) name of the form control to register with the parent WnlGroup

  readonly wnlDefault = input<any>(); // value considered WNL
  readonly wnlClearValue = input<any>(); // value considered "untouched"

  readonly wnlFormControl = input<UntypedFormControl>(); // used for fields whose values are calculated from other fields

  readonly wnlIsWnl = input<(value: any) => boolean>(); // custom function to determine if value is WNL (overrides default equality check)

  readonly wnlApplyStrategy = input<'apply' | 'keep'>('apply'); // whether setting all to WNL should affect this form
  readonly wnlClearStrategy = input<'clear' | 'keep'>('clear'); // whether setting all to non-WNL should affect this form

  readonly wnlTrackPristine = input(true);


  private unregisterControl?: () => void;
  private valueChangeSub?: Subscription;


  private wnlGroup = inject(WnlGroupDirective, { optional: true })!;
  private host = inject<WnlHostControl>(WNL_HOST_CONTROL, { optional: true, self: true });

  constructor() { }


  ngOnInit(): void {
    this.reRegister();
  }

  ngOnChanges(): void {
    this.reRegister();
  }

  ngOnDestroy(): void {
    this.unregisterControl?.();
    this.valueChangeSub?.unsubscribe();
  }


  private reRegister() {
    // clear previous registration (if any)
    this.unregisterControl?.();
    this.valueChangeSub?.unsubscribe();

    const hasControl = this.getControl() !== null;
    if (!this.wnlGroup || !this.controlKey() || !hasControl) {
      return;
    }

    const control = this.getControl()!;
    const initialValue = this.cloneValue(control.value);

    // create control profile detailing how the control interacts with the WNL group
    const controlProfile = {
      key: this.controlKey(),
      isWnl: () => {
        const customWnlFn = this.wnlIsWnl();
        return customWnlFn ? customWnlFn(control.value) : this.isWnlValue(control.value);
      },
      isPristine: () => {
        return !this.wnlTrackPristine() || this.compareValues(control.value, initialValue);
      },
      applyWnl: () => {
        if (this.wnlApplyStrategy() === 'keep') { return; }

        control.enable({ onlySelf: true, emitEvent: false });
        control.setValue(this.getDefaultValue(), { onlySelf: true, emitEvent: false });
      },
      clearWnl: () => {
        if (this.wnlClearStrategy() === 'keep') { return; }

        control.enable({ onlySelf: true, emitEvent: false });
        control.setValue(this.getClearValue(), { onlySelf: true, emitEvent: false });
      },
    }

    // register with parent WNL group, which returns unregistration function
    const unregisterFn = this.wnlGroup.registerControl(controlProfile);
    this.unregisterControl = unregisterFn;

    // subscribe to value changes to notify parent WNL group
    this.valueChangeSub = this.getControl()?.valueChanges.subscribe(() => {
      this.wnlGroup?.notifyStateChanged();
    });
  }


  // gets form control (checkboxes require special behavior)
  private getControl(): UntypedFormControl | null {
    const formControl = this.wnlFormControl();
    if (formControl) {
      return formControl;
    }

    return this.host?.getWnlControl() ?? null;
  }


  // gets the default value for the control
  private getDefaultValue() {
    const wnlDefaultValue = this.wnlDefault();
    if (wnlDefaultValue !== undefined) {
      return wnlDefaultValue;
    }

    if (this.host?.getWnlHostType() === 'checkbox-group') {
      return [];
    }

    if (this.host?.getWnlHostType() === 'input') {
      return '';
    }

    return true; // most likely an independent checkbox
  }


  private getClearValue() {
    const wnlClearValue = this.wnlClearValue();
    if (wnlClearValue !== undefined) {
      return wnlClearValue;
    }

    if (this.host?.getWnlHostType() === 'checkbox') {
      return false;
    }

    if (this.host?.getWnlHostType() === 'checkbox-group') {
      return [];
    }

    return '';
  }



  // determines if current value is WNL (following 3 functions are helpers)
  private isWnlValue(value: any): boolean {
    return this.compareValues(value, this.getDefaultValue());
  }

  // compares values for equality
  private compareValues(value: any, expected: any): boolean {
    // sanitize values
    const normalizedValue = this.normalizeValueForComparison(value);
    const normalizedExpected = this.normalizeValueForComparison(expected);

    // if arrays, check length and items (order doesn't matter)
    if (Array.isArray(normalizedValue) && Array.isArray(normalizedExpected)) {
      return this.arraysEqual(normalizedValue, normalizedExpected);
    }

    // otherwise, normal comparison works
    return normalizedValue === normalizedExpected;
  }

  // compares two arrays for equality, ignoring order (assumes primitive values)
  private arraysEqual(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }

    const sortedValue = [...arr1].sort();
    const sortedExpected = [...arr2].sort();
    return sortedValue.every((item, index) => item === sortedExpected[index]);
  }

  // basic value sanitization using common cases observed in form controls, can be expanded as needed
  private normalizeValueForComparison(value: any): any {
    // catch error case for checkboxes
    const isEmrCheckbox = this.host?.getWnlHostType() === 'checkbox';
    const invalidCheckboxValue = isEmrCheckbox && (value === null || value === undefined);
    if (invalidCheckboxValue) {
      return false;
    }

    // catch error or special case for selection inputs
    const isEmrInputSelect = this.host?.getWnlHostType() === 'input' && this.host.isWnlSelectInput?.() === true;
    const invalidSelectValue = isEmrInputSelect && (value === null || value === undefined);
    const specialSelectValue = isEmrInputSelect && value === '__reset__';
    if (invalidSelectValue || specialSelectValue) {
      return '';
    }

    // neither error nor special cases detected
    return value;
  }



  private cloneValue(value: any): any {
    if (Array.isArray(value)) {
      return [...value];
    }

    return value;
  }
}
